<template>
  <div class="mt-5">
    <b-container fluid>
      <div class="card mb-3">
        <div class="card-body py-2 px-3">
          <div class="row align-items-end">
            <div class="col-md-2">
              <div class="form-group m-0 p-0">
                <label class="small mb-0">Programme Type</label>
                <select
                  class="form-control form-control-sm"
                  v-model="filterProgrammeType"
                  @change="loadBilling"
                >
                  <option value="">All</option>
                  <option
                    v-for="(d, index) in degreeOptions"
                    :key="index"
                    :value="d.programme_type"
                  >
                    {{ d.programme_type }}
                  </option>
                </select>
              </div>
            </div>
            <div class="col-md-2">
              <div class="form-group m-0 p-0">
                <label class="small mb-0">Fee Type</label>
                <select
                  class="form-control form-control-sm"
                  v-model="filterType"
                  @change="loadBilling"
                >
                  <option value="">All</option>
                  <option value="APPLICATION">Application</option>
                  <option value="ACCEPTANCE">Acceptance</option>
                  <option value="CAUTION">Caution</option>
                  <option value="STUDENT">Student</option>
                </select>
              </div>
            </div>
            <div class="col-md-2">
              <div class="form-group m-0 p-0">
                <label class="small mb-0">Session</label>
                <select
                  class="form-control form-control-sm"
                  v-model="filterSession"
                  @change="loadBilling"
                >
                  <option value="">All</option>
                  <option v-for="s in sessionOptions" :key="s" :value="s">{{ s }}</option>
                </select>
              </div>
            </div>
            <div class="col-md-2">
              <div class="form-group m-0 p-0">
                <label class="small mb-0">Grant Type</label>
                <select
                  class="form-control form-control-sm"
                  v-model="filterGrantType"
                  @change="loadBilling"
                >
                  <option value="">All</option>
                  <option value="RUN">RUN</option>
                  <option value="OTHERS">OTHERS</option>
                </select>
              </div>
            </div>
            <div class="col-md-2">
              <b-form-group label="Search" label-size="sm" class="mb-0">
                <b-form-input
                  v-model="filter"
                  type="search"
                  size="sm"
                  placeholder="Search..."
                ></b-form-input>
              </b-form-group>
            </div>
            <div class="col-md-2 text-right align-self-end">
              <button class="btn btn-success btn-sm" @click="openCreateModal">
                <i class="fas fa-plus"></i> Add New Fee
              </button>
            </div>
          </div>
        </div>
      </div>

      <b-table
        :busy="isBusy"
        :items="items"
        :fields="fields"
        :filter="filter"
        :current-page="currentPage"
        :per-page="perPage"
        stacked="md"
        show-empty
        small
        hover
        @filtered="onFiltered"
      >
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong>Loading...</strong>
          </div>
        </template>

        <template #cell(amount)="{ value }">
          {{ formatCurrency(value) }}
        </template>

        <template #cell(optional)="{ value }">
          <b-badge :variant="value ? 'warning' : 'primary'">
            {{ value ? "Optional" : "Compulsory" }}
          </b-badge>
        </template>

        <template #cell(actions)="row">
          <b-button size="sm" variant="info" class="mr-1" @click="openEditModal(row.item)">
            <i class="fas fa-edit"></i>
          </b-button>
          <b-button size="sm" variant="danger" @click="confirmDelete(row.item)">
            <i class="fas fa-trash"></i>
          </b-button>
        </template>
      </b-table>

      <b-row>
        <b-col md="6" class="my-1">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            class="my-0"
          ></b-pagination>
        </b-col>
        <b-col md="6" class="my-1 text-right">
          <small class="text-muted">{{ totalRows }} record(s)</small>
        </b-col>
      </b-row>

      <!-- Create/Edit Modal -->
      <b-modal
        v-model="showModal"
        :title="editMode ? 'Edit Fee Definition' : 'Create Fee Definition'"
        size="lg"
        hide-footer
        centered
      >
        <form @submit.prevent="saveBilling">
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>Fee Type <span class="text-danger">*</span></label>
                <select class="form-control form-control-sm" v-model="form.type" required>
                  <option value="">Select</option>
                  <option value="APPLICATION">Application</option>
                  <option value="ACCEPTANCE">Acceptance</option>
                  <option value="CAUTION">Caution</option>
                  <option value="STUDENT">Student</option>
                </select>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>Amount <span class="text-danger">*</span></label>
                <input
                  type="number"
                  class="form-control form-control-sm"
                  v-model="form.amount"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>Programme Type <span class="text-danger">*</span></label>
                <select class="form-control form-control-sm" v-model="form.programme_type" required>
                  <option value="">Select</option>
                  <option
                    v-for="(d, index) in degreeOptions"
                    :key="index"
                    :value="d.programme_type"
                  >
                    {{ d.programme_type }}
                  </option>
                </select>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>Programme</label>
                <select class="form-control form-control-sm" v-model="form.programme_id">
                  <option value="">None</option>
                  <option
                    v-for="(p, index) in programmeOptions"
                    :key="index"
                    :value="p.id"
                  >
                    {{ p.programme }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>Session</label>
                <select class="form-control form-control-sm" v-model="form.session">
                  <option value="">None</option>
                  <option v-for="(s, index) in sessionOptions" :key="index">
                    {{ s }}
                  </option>
                </select>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>Installment</label>
                <input
                  type="number"
                  class="form-control form-control-sm"
                  v-model="form.installment"
                  min="0"
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>Description</label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model="form.description"
                />
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <label>Grant Type</label>
                <select class="form-control form-control-sm" v-model="form.grant_type">
                  <option value="">None</option>
                  <option value="RUN">RUN</option>
                  <option value="OTHERS">OTHERS</option>
                </select>
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <label>Optional?</label>
                <select class="form-control form-control-sm" v-model="form.optional">
                  <option :value="0">No (Compulsory)</option>
                  <option :value="1">Yes (Optional)</option>
                </select>
              </div>
            </div>
          </div>
          <div class="text-right mt-3">
            <button type="button" class="btn btn-secondary mr-2" @click="showModal = false">
              Cancel
            </button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              <b-spinner small v-if="saving"></b-spinner>
              {{ editMode ? "Update" : "Create" }}
            </button>
          </div>
        </form>
      </b-modal>
    </b-container>
  </div>
</template>

<script>
import { mapState } from "vuex";
export default {
  data() {
    return {
      isBusy: false,
      saving: false,
      items: [],
      filter: "",
      filterProgrammeType: "",
      filterType: "",
      filterSession: "",
      filterGrantType: "",
      currentPage: 1,
      perPage: 15,
      totalRows: 0,
      showModal: false,
      editMode: false,
      editId: null,
      form: this.emptyForm(),
      fields: [
        { key: "id", label: "ID", sortable: true },
        { key: "type", label: "Fee Type", sortable: true },
        { key: "amount", label: "Amount", sortable: true },
        { key: "programme_type", label: "Programme Type", sortable: true },
        { key: "session", label: "Session", sortable: true },
        { key: "description", label: "Description", sortable: true },
        { key: "installment", label: "Installment", sortable: true },
        { key: "grant_type", label: "Grant Type", sortable: true },
        { key: "optional", label: "Optional", sortable: true },
        { key: "actions", label: "Actions" },
      ],
    };
  },
  computed: {
    ...mapState({ settings: (state) => state.settings }),
    sessionOptions() {
      if (!this.settings || !this.settings.length) return [];
      return [...new Set(this.settings.map((s) => s.session_name))];
    },
    degreeOptions() {
      return this.$store.state.programmeTypes || [];
    },
    programmeOptions() {
      return this.$store.state.programmes || [];
    },
  },
  created() {
    this.$store.dispatch("getSettings");
    this.$store.dispatch("getprogrammeTypes");
    this.$store.dispatch("getProgrammes");
    this.loadBilling();
  },
  methods: {
    emptyForm() {
      return {
        type: "",
        amount: "",
        programme_type: "",
        programme_id: "",
        session: "",
        description: "",
        installment: "",
        grant_type: "",
        optional: 0,
      };
    },
    formatCurrency(val) {
      return "₦" + Number(val || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 });
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    loadBilling() {
      this.isBusy = true;
      let params = {};
      if (this.filterProgrammeType) params.programme_type = this.filterProgrammeType;
      if (this.filterType) params.type = this.filterType;
      if (this.filterSession) params.session = this.filterSession;
      if (this.filterGrantType) params.grant_type = this.filterGrantType;

      axios
        .get("admin/all/payment", { params })
        .then((response) => {
          this.items = response.data.payment || [];
          this.totalRows = this.items.length;
        })
        .catch(() => {
          Toast.fire({ icon: "error", title: "Failed to load billing data" });
        })
        .finally(() => {
          this.isBusy = false;
        });
    },
    openCreateModal() {
      this.editMode = false;
      this.editId = null;
      this.form = this.emptyForm();
      this.showModal = true;
    },
    openEditModal(item) {
      this.editMode = true;
      this.editId = item.id;
      this.form = {
        type: item.type || "",
        amount: item.amount || "",
        programme_type: item.programme_type || "",
        programme_id: item.programme_id || "",
        session: item.session || "",
        description: item.description || "",
        installment: item.installment || "",
        grant_type: item.grant_type || "",
        optional: item.optional ? 1 : 0,
      };
      this.showModal = true;
    },
    saveBilling() {
      this.saving = true;
      let request;
      if (this.editMode) {
        request = axios.put(`admin/bursary/billing/${this.editId}`, this.form);
      } else {
        request = axios.post("admin/bursary/billing", this.form);
      }

      request
        .then(() => {
          Toast.fire({
            icon: "success",
            title: this.editMode ? "Fee updated successfully" : "Fee created successfully",
          });
          this.showModal = false;
          this.loadBilling();
        })
        .catch((err) => {
          const msg =
            err.response && err.response.data && err.response.data.error
              ? typeof err.response.data.error === "string"
                ? err.response.data.error
                : "Validation error"
              : "Operation failed";
          Toast.fire({ icon: "error", title: msg });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    confirmDelete(item) {
      Swal.fire({
        title: "Delete this fee?",
        text: `${item.type} - ${item.programme_type} (₦${item.amount})`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, delete",
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteBilling(item.id);
        }
      });
    },
    deleteBilling(id) {
      axios
        .delete(`admin/bursary/billing/${id}`)
        .then(() => {
          Toast.fire({ icon: "success", title: "Fee deleted successfully" });
          this.loadBilling();
        })
        .catch((err) => {
          const msg =
            err.response && err.response.data && err.response.data.error
              ? err.response.data.error
              : "Delete failed";
          Toast.fire({ icon: "error", title: msg });
        });
    },
  },
};
</script>
